import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

/**
 * App-brede, gestileerde bevestigingsdialoog (R11) als async vervanger voor window.confirm.
 *
 *   const confirm = useConfirm()
 *   if (await confirm({ title, message, confirmText, cancelText, danger })) { ... }
 *
 * useDiscardConfirm() geeft een kant-en-klare "niet-opgeslagen wijzigingen"-bevestiging.
 */
const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const { t } = useTranslation()
  const [state, setState] = useState(null)
  const resolver = useRef(null)

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve
    setState({
      title: t('common.confirmTitle', { defaultValue: 'Bevestigen' }),
      message: '',
      confirmText: t('common.confirm', { defaultValue: 'OK' }),
      cancelText: t('common.cancel', { defaultValue: 'Annuleren' }),
      danger: false,
      ...opts,
    })
  }), [t])

  const settle = (val) => {
    setState(null)
    if (resolver.current) { resolver.current(val); resolver.current = null }
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) settle(false) }}
        >
          <div className="bg-surface dark:bg-gray-800 rounded-card shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <div className={`shrink-0 mt-0.5 ${state.danger ? 'text-red-500' : 'text-accent'}`}>
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-ink dark:text-white">{state.title}</h3>
                {state.message && <p className="text-sm text-muted mt-1 whitespace-pre-line">{state.message}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => settle(false)}
                className="px-4 py-2 border border-line dark:border-gray-600 rounded-control hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold text-ink dark:text-white"
              >
                {state.cancelText}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => settle(true)}
                className={`px-4 py-2 rounded-control font-semibold text-white ${state.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

/**
 * Async confirm: `const confirm = useConfirm(); if (await confirm({...})) ...`.
 * Zonder ConfirmProvider boven de component geeft de hook null (createContext(null)) en is een
 * aanroep een luide TypeError: geen stille no-op (#975). main.jsx zet de provider rond de app.
 */
export function useConfirm() {
  return useContext(ConfirmContext)
}

export default ConfirmProvider
